import React, { useEffect } from 'react'
import { TextField } from '@mui/material'
import { DateTimePicker as MuiDateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { TimePicker as MuiTimePicker } from '@mui/x-date-pickers/TimePicker'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { DateTime } from 'luxon'
import { FieldProps } from 'formik'
import { GenericFormProps, getError, hasError, isViewOnly } from 'src/form/helpers'

type WrapperProps = {
  disabled?: boolean
  fullWidth?: boolean
  helperText?: string
  label?: string
  timeOnly?: boolean
  referenceDateString?: string
}

export const CustomDateTimePicker: React.FC<WrapperProps & GenericFormProps & FieldProps> = (props) => {
  const viewOnly = isViewOnly(props)
  const error = hasError(props)
  const errorText = error ? getError(props) : props.helperText

  const {
    disabled,
    fullWidth,
    label,
    field: { name, value },
    form: { setFieldValue },
    timeOnly,
    referenceDateString,
  } = props

  const handleTimeOnlyChange = (newValue: DateTime | null) => {
    if (!newValue?.isValid) {
      setFieldValue(name, null)
      return
    }
    const base = referenceDateString ? DateTime.fromISO(referenceDateString) : null
    // With no date to attach to yet, keep the time on the day the picker gave it; the
    // effect below moves it onto the date once one is picked.
    const combinedLocal = (base?.isValid ? base : newValue).set({
      hour: newValue.hour,
      minute: newValue.minute,
      second: newValue.second ?? 0,
      millisecond: 0,
    })
    setFieldValue(name, combinedLocal.toUTC().toISO())
  }

  useEffect(() => {
    if (!referenceDateString) return
    if (!value) return

    const timeLocal = DateTime.fromISO(value).setZone('local')
    const dateLocal = DateTime.fromISO(referenceDateString).setZone('local')
    if (!timeLocal.isValid || !dateLocal.isValid) return

    const combinedLocal = DateTime.local(
      dateLocal.year,
      dateLocal.month,
      dateLocal.day,
      timeLocal.hour,
      timeLocal.minute,
      timeLocal.second ?? 0,
      0
    )

    const nextISO = combinedLocal.toUTC().toISO()
    if (nextISO !== value) setFieldValue(name, nextISO)
  }, [referenceDateString, value, name, setFieldValue])

  if (timeOnly) {
    return (
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <MuiTimePicker
          ampm
          disabled={viewOnly || !!disabled}
          label={label}
          value={fromIsoToLocalDateTime(value)}
          onChange={(newValue) => handleTimeOnlyChange(newValue)}
          renderInput={(inputProps) => (
            <TextField
              {...inputProps}
              error={error}
              fullWidth={!!fullWidth}
              helperText={errorText}
              margin="normal"
              name={name}
              size="small"
              variant="outlined"
            />
          )}
        />
      </LocalizationProvider>
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <MuiDateTimePicker
        ampm
        disabled={viewOnly || !!disabled}
        label={label}
        value={fromIsoToLocalDateTime(value)}
        onChange={(newValue) => {
          if (newValue?.isValid) {
            setFieldValue(name, newValue.toUTC().toISO())
          } else {
            setFieldValue(name, null)
          }
        }}
        renderInput={(inputProps) => (
          <TextField
            {...inputProps}
            error={error}
            fullWidth={!!fullWidth}
            helperText={errorText}
            margin="normal"
            name={name}
            size="small"
            variant="outlined"
          />
        )}
      />
    </LocalizationProvider>
  )
}

function fromIsoToLocalDateTime(iso?: string | null) {
  return iso ? DateTime.fromISO(iso).toLocal() : null
}
